import { csvZeile, feldText, feldZahl, FELD_LEER, pruefeFeldzahl, } from './csv.js';
import { kuerzeAufZeichen, nachZielkodierung } from './kodierung.js';
import { SACHKONTENLAENGE } from './felder.js';
/**
 * Die zwei Stammdatenlieferungen — Chip `DX-stamm`, Task 7 des Plans
 * `docs/superpowers/plans/2026-09-08-datev-export-umsetzung.md`.
 *
 * Feldtafel `docs/datev/extf-felder-stammdaten.md` (Datenkategorie 16
 * Debitoren/Kreditoren, Datenkategorie 20 Sachkontenbeschriftungen), Spec
 * `docs/superpowers/specs/2026-09-08-datev-export-design.md` Abschnitt 3.1
 * (die fuenf Lieferungen) und 6 (Personenkonten), `D-6`, `D-14`, `D-16`,
 * `D-18`, `D-34`.
 *
 * ===========================================================================
 * WARUM DIESE LIEFERUNG VOR DEM ERSTEN STAPEL KOMMT
 * ===========================================================================
 *
 * **Ein Stapel, der auf ein Personenkonto bucht, das die Kanzlei nicht kennt,
 * wird beim Import zur Korrektur** — und zwar zu genau der Sorte, die die
 * Messgroesse des Nordsterns zaehlt. Deshalb ist die Reihenfolge des
 * allerersten Exports zwingend (`D-16`): Stammdaten, dann Saldenvortrag, dann
 * Buchungsstapel Periode 1.
 *
 * ===========================================================================
 * DIE ARBEITSTEILUNG DIESER DATEI
 * ===========================================================================
 *
 * Hier steht **nur, was ohne Datenbank entscheidbar ist**: die Feldreihenfolge
 * beider Kategorien, die Kreispruefung, der Kodierungsriegel und die Regel,
 * wann ein Lauf voll und wann er nur die geaenderten Saetze traegt. Woher ein
 * Kundenname, eine Kontoversion oder der Zeitpunkt des letzten Laufs kommen,
 * steht in `apps/api/src/datev/stammdaten.ts` und
 * `apps/api/src/datev/kontenbeschriftung.ts`.
 *
 * Die Kopfzeile baut keine dieser Funktionen selbst: Sie kommt aus
 * `kopfzeile.ts` mit der Datenkategorie `debitoren_kreditoren` bzw.
 * `kontenbeschriftungen` — dieselbe Zeile, dieselben 31 Felder, nur die
 * Felder 3, 4 und 5 wechseln.
 */
// ===========================================================================
// 1. DIE PERSONENKONTENKREISE — `D-34`
// ===========================================================================
/**
 * Ein Personenkonto ist genau **Sachkontenlaenge + 1** Stellen lang.
 *
 * Abgeleitet und nicht hingeschrieben: {@link SACHKONTENLAENGE} ist Feld 14
 * der Kopfzeile und muss der Einrichtung des Mandanten in DATEV entsprechen.
 * Eine zweite, unabhaengig gepflegte `5` waere die Stelle, an der beide
 * auseinanderlaufen, ohne dass ein Lauf es merkt.
 */
export const PERSONENKONTENLAENGE = Number(SACHKONTENLAENGE) + 1;
/**
 * Debitoren `10000-69999`, Kreditoren `70000-99999` (`D-34`).
 *
 * **Die Zahlen stehen seit Migration `0013` in `number_range` und werden
 * nicht angefasst.** Sie sind die DATEV-Voreinstellung bei vierstelligen
 * Sachkonten, und 1.511 Personenkonten sind bereits vergeben; eine Abweichung
 * waere teuer. Dass sie hier ein zweites Mal stehen, ist Absicht und keine
 * Doppelung: Diese Datei kennt keine Datenbank, und die Naht haelt die
 * gelesenen Grenzen gegen diese hier (`E-1025`) — zwei Quellen, die einander
 * pruefen, statt einer, der man glauben muss.
 */
export const DEBITORENKREIS = {
    art: 'debitor',
    von: 10_000,
    bis: 69_999,
};
/** Der Kreditorenkreis, gebaut und unbebucht (Spec 6.3). */
export const KREDITORENKREIS = {
    art: 'kreditor',
    von: 70_000,
    bis: 99_999,
};
/** Ein Personenkonto, das so nicht hinausgehen darf. */
export class DatevPersonenkontofehler extends Error {
    kontonummer;
    constructor(kontonummer, grund) {
        super(`Das Personenkonto ${JSON.stringify(kontonummer)} geht so nicht hinaus: ${grund} ` +
            `Ein Stapel, der auf ein Konto bucht, das die Kanzlei nicht kennt oder das ` +
            `drueben ein Sachkonto ist, wird beim Import zur Korrektur.`);
        this.kontonummer = kontonummer;
        this.name = 'DatevPersonenkontofehler';
    }
}
/**
 * Haelt ein Personenkonto gegen seinen Kreis (`E-1025`).
 *
 * Drei Bedingungen, und jede faengt einen anderen Fehler:
 *
 * 1. **Genau {@link PERSONENKONTENLAENGE} Ziffern.** Vier Stellen waeren
 *    drueben ein Sachkonto, sechs kein gueltiges Konto. Fuehrende Nullen sind
 *    damit ebenfalls ausgeschlossen — `010000` und `10000` waeren zwei
 *    Schreibweisen desselben Kontos und in einer Datei nicht mehr
 *    unterscheidbar.
 * 2. **Innerhalb des Kreises.** `number_range` haelt das an der Vergabe; hier
 *    wird es an der Ausgabe noch einmal gehalten, weil zwischen beiden ein
 *    Altdatenimport liegt, der Nummern mitbringt, die er nicht selbst
 *    vergeben hat.
 * 3. **Keine Kollision mit einem Sachkonto.** Die Sachkontennummern kommen
 *    aus `account` und werden uebergeben, nicht geraten: Ein fuenfstelliges
 *    Sachkonto ist nach der Einrichtung des Mandanten zwar nicht vorgesehen,
 *    aber diese Datei kann es nicht wissen — und ein Personenkonto, das
 *    drueben ein Sachkonto trifft, bucht auf das falsche.
 */
export function pruefePersonenkonto(kontonummer, kreis, sachkontonummern) {
    if (!new RegExp(`^\\d{${PERSONENKONTENLAENGE}}$`).test(kontonummer)) {
        throw new DatevPersonenkontofehler(kontonummer, `Ein Personenkonto traegt genau ${PERSONENKONTENLAENGE} Ziffern ` +
            `(Sachkontenlaenge ${SACHKONTENLAENGE} plus eins), ohne fuehrende Null.`);
    }
    const wert = Number(kontonummer);
    if (wert < kreis.von || wert > kreis.bis) {
        throw new DatevPersonenkontofehler(kontonummer, `Es liegt ausserhalb des ` +
            `${kreis.art === 'debitor' ? 'Debitorenkreises' : 'Kreditorenkreises'} ` +
            `${kreis.von}-${kreis.bis} (D-34, Migration 0013).`);
    }
    if (sachkontonummern.has(kontonummer)) {
        throw new DatevPersonenkontofehler(kontonummer, 'Unter derselben Nummer steht ein Sachkonto im Kontenrahmen.');
    }
}
// ===========================================================================
// 2. DEBITOREN/KREDITOREN — DATENKATEGORIE 16, FORMATVERSION 5
// ===========================================================================
/**
 * Die Zahl der Felder eines Stammsatzes.
 *
 * `docs/datev/extf-felder-stammdaten.md`: Die Kategorie ist von 164 Feldern
 * (aeltere Fassung) auf **254** gewachsen; der Zuwachs steckt in fuenf
 * weiteren Bankverbindungen und in den Mahnfristen am Ende.
 */
export const DEBITOR_KREDITOR_FELDANZAHL = 254;
/**
 * Die Stelle jedes belegten Feldes, **einsbasiert wie die amtliche
 * Zaehlung** — dieselbe Zaehlweise wie in `felder.ts`.
 *
 * **Feld 33, die E-Mail-Adresse, steht hier und wird trotzdem nicht
 * gefuellt** (der Plan-Nachtrag zu Task 7 traegt die Begruendung). Sie steht
 * hier, weil die Entscheidung, sie NICHT zu schreiben, an einem Namen haengen
 * soll und nicht an einer Leerstelle, die spaeter jemand fuer ein Versehen
 * haelt.
 */
export const DEBITOR_STELLE = {
    konto: 1,
    nameUnternehmen: 2,
    nameNatuerlichePerson: 4,
    vornameNatuerlichePerson: 5,
    nameKeineAngabe: 6,
    adressatentyp: 7,
    euLand: 9,
    euUstId: 10,
    strasse: 16,
    postleitzahl: 18,
    ort: 19,
    land: 20,
    email: 33,
    kundennummer: 99,
    sprache: 101,
    diverseKonto: 105,
};
/**
 * Feld 7: `0` keine Angabe, `1` natuerliche Person, `2` Unternehmen.
 *
 * **Warum ein Privatkunde hier `0` bekommt und nicht `1`.** Die Felder 4 und 5
 * verlangen Nachname und Vorname **getrennt**; dieses System fuehrt genau ein
 * Namensfeld (`customer.legal_name`). Eine Zeichenkette am letzten Leerzeichen
 * zu teilen ist geraten, und ein falsch geteilter Name ist drueben eine
 * Korrektur — bei Doppelnamen, Namenszusaetzen und Firmierungen, die wie ein
 * Personenname aussehen, faellt der Fehler zudem nicht auf. `0` mit dem
 * vollstaendigen Namen in Feld 6 sagt genau das, was wir wissen.
 */
export const ADRESSATENTYP = {
    keineAngabe: '0',
    natuerlichePerson: '1',
    unternehmen: '2',
};
/** Feld 101: `1` Deutsch. Die Kanzlei liest deutsch, und der Beleg ist deutsch. */
export const SPRACHE_DEUTSCH = '1';
/**
 * Feld 105: `0` — kein Diverse-Konto.
 *
 * Ein Diverse-Konto sammelt Geschaeftspartner ohne eigenes Personenkonto.
 * Genau diesen Zustand schafft `D-6` ab.
 */
export const DIVERSE_KONTO_NEIN = '0';
/** Die Laengengrenzen der Felder, die wir fuellen (Feldtafel Kategorie 16). */
export const DEBITOR_MAX_ZEICHEN = {
    nameUnternehmen: 50,
    nameKeineAngabe: 50,
    strasse: 36,
    postleitzahl: 10,
    ort: 30,
    euUstId: 13,
    kundennummer: 15,
};
/**
 * Die Feldnamen, die die eigene Feldtafel nennt.
 *
 * `[UNGEKLAERT]` **Vollstaendig ist die ZAHL der Felder, nicht jeder NAME.**
 * Die eigene Feldtafel nennt bewusst nur den Ausschnitt, den wir fuellen —
 * eine Tabelle ueber alle 254 Felder waere eine seitenweise Uebernahme des
 * fremden Werkes (`docs/datev/README.md`, Urheberrechtsregel). Jede Stelle,
 * deren Namen die Tafel nennt, traegt ihn; jede andere traegt `Feld <n>` und
 * ist damit als **nicht belegt** erkennbar, statt einen erfundenen Namen zu
 * behaupten.
 *
 * **Warum das tragbar ist:** Die Datenzeilen sind stellenbezogen — Feld 33 ist
 * das dreiunddreissigste semikolongetrennte Feld und nicht das Feld, ueber dem
 * `E-Mail` steht. Prueft die Kanzleisoftware die Ueberschriftenzeile dennoch,
 * wird die Datei beim Probelauf (`DX-probelauf`) **abgewiesen** und nicht
 * still falsch verbucht — dieselbe Abwaegung, mit der
 * `docs/datev/extf-felder-kopfzeile.md` die Formatversion `13` festschreibt.
 */
const DEBITOR_FELDNAME_JE_STELLE = new Map([
    [1, 'Konto'],
    [2, 'Name (Adressatentyp Unternehmen)'],
    [3, 'Unternehmensgegenstand'],
    [4, 'Name (Adressatentyp natuerl. Person)'],
    [5, 'Vorname (Adressatentyp natuerl. Person)'],
    [6, 'Name (Adressatentyp keine Angabe)'],
    [7, 'Adressatentyp'],
    [8, 'Kurzbezeichnung'],
    [9, 'EU-Land'],
    [10, 'EU-UStID'],
    [11, 'Anrede'],
    [15, 'Adressart'],
    [16, 'Strasse'],
    [17, 'Postfach'],
    [18, 'Postleitzahl'],
    [19, 'Ort'],
    [20, 'Land'],
    [26, 'Kennz. Korrespondenzadresse'],
    [27, 'Adresse gueltig von'],
    [28, 'Adresse gueltig bis'],
    [33, 'E-Mail'],
    [99, 'Kundennummer'],
    [101, 'Sprache'],
    [105, 'Diverse-Konto'],
    [108, 'Kreditlimit (Debitor)'],
    [109, 'Zahlungsbedingung'],
    [250, 'Fristberechnung'],
    [251, 'Mahnfrist 1'],
    [252, 'Mahnfrist 2'],
    [253, 'Mahnfrist 3'],
    [254, 'Letzte Frist'],
]);
/** Die 254 Feldnamen der Datenkategorie 16, in ihrer Reihenfolge. */
export const DEBITOR_KREDITOR_FELDER = Array.from({ length: DEBITOR_KREDITOR_FELDANZAHL }, (_, stelle) => DEBITOR_FELDNAME_JE_STELLE.get(stelle + 1) ?? `Feld ${stelle + 1}`);
/**
 * Die Zahl der Stellen, deren Namen die eigene Feldtafel nicht nennt.
 *
 * Steht hier, damit die Groesse der benannten Grenze eine **Zahl** ist und
 * kein Gefuehl.
 */
export const DEBITOR_FELDNAMEN_UNGEKLAERT = DEBITOR_KREDITOR_FELDANZAHL - DEBITOR_FELDNAME_JE_STELLE.size;
/**
 * Zerlegt eine USt-IdNr. in Laenderkuerzel und Nummer (Felder 9 und 10).
 *
 * **Die Nummer geht OHNE Laenderkuerzel hinaus, und das ist nachgerechnet und
 * nicht angenommen:** Feld 10 nimmt hoechstens 13 Zeichen. Die laengste
 * USt-IdNr. der Union hat zwei Buchstaben Kuerzel und zwoelf Stellen Nummer,
 * zusammen vierzehn — mit Kuerzel passte sie nicht ins Feld, ohne Kuerzel
 * bleibt eine Stelle frei. Dieselbe Rechnung steckt in
 * `vat_id_wellformed_v1` (Migration `0011`): `^[A-Z]{2}[A-Z0-9]{2,12}$`.
 *
 * `null` fuer alles, was nicht die Form einer USt-IdNr. hat. Die Form wird an
 * der Systemgrenze schon durchgesetzt (`customer_vat_id_wellformed`); hier
 * wird sie nicht ein zweites Mal beurteilt, sondern nur getrennt — was
 * durchkaeme, ohne dass beide sich einig sind, geht als Leerstelle hinaus und
 * nicht als halbe Angabe.
 */
export function zerlegeUstIdNr(ustIdNr) {
    if (ustIdNr === null)
        return null;
    const treffer = /^([A-Z]{2})([A-Z0-9]{2,12})$/.exec(ustIdNr.trim());
    if (treffer === null)
        return null;
    return { land: treffer[1] ?? '', nummer: treffer[2] ?? '' };
}
/**
 * Kuerzt ein Textfeld auf seine Laenge und meldet, wenn dabei etwas wegfiel.
 *
 * **Gekuerzt und gemeldet, nicht abgebrochen.** Das Format laesst laengere
 * Werte nicht zu; ein Abbruch wegen eines um zwei Zeichen zu langen Ortsnamens
 * hielte den ganzen Export auf. Ob eine Kuerzung den Lauf blockt, entscheidet
 * die Vorpruefung (Task 12) und nicht der Erzeuger — dieselbe Trennung wie in
 * `apps/api/src/datev/satzbau.ts`: Eine Uebersetzung, die selbst ueber das
 * Ausliefern entscheidet, hat zwei Aufgaben und wird bei der zweiten
 * nachgiebig.
 *
 * **Der Unterschied zur Kodierung ist kein Widerspruch.** Eine Kuerzung ist
 * sichtbar — der Name endet mitten im Wort, und drueben faellt das auf. Ein
 * still ersetztes Zeichen ist es nicht: Ein Name, der fast stimmt, faellt
 * niemandem auf. Deshalb bricht das eine ab und das andere meldet.
 */
function feldGekuerzt(konto, feldname, stelle, wert, maxZeichen, befunde) {
    const gekuerzt = kuerzeAufZeichen(wert, maxZeichen);
    if (gekuerzt !== wert) {
        befunde.push({
            konto,
            feld: feldname,
            stelle,
            meldung: `${feldname} (Feld ${stelle}) nimmt höchstens ${maxZeichen} Zeichen; der Wert ` +
                `${JSON.stringify(wert)} ist ${[...wert].length} Zeichen lang und geht als ` +
                `${JSON.stringify(gekuerzt)} hinaus.`,
        });
    }
    return gekuerzt;
}
/**
 * Haelt jeden Text eines Stammsatzes gegen die Zielkodierung (`E-1028`).
 *
 * **Geprueft wird je Feld und nicht an der fertigen Zeile.** Beides fuehrte
 * zum Abbruch; nur das erste nennt in seiner Meldung einen Text, in dem ein
 * Mensch die Stelle wiederfindet. Die Zeile eines Stammsatzes ist 253
 * Semikola lang.
 *
 * Geprueft wird **vor** dem Kuerzen: Ein nicht kodierbares Zeichen jenseits
 * der Feldgrenze verschwaende sonst still, und der naechste Kunde mit einem
 * kuerzeren Namen brachte denselben Abbruch zu einem Zeitpunkt, an dem
 * niemand mehr an diesen Satz denkt.
 */
function pruefeKodierung(satz) {
    for (const wert of [satz.name, satz.strasse, satz.postleitzahl, satz.ort, satz.land]) {
        nachZielkodierung(wert);
    }
}
/**
 * Baut die 254 Felder eines Debitoren-/Kreditorenstammsatzes.
 *
 * **Was ausdruecklich leer bleibt, und warum jedes davon eine Entscheidung
 * ist:**
 *
 * - **Die Bankverbindungen** (Felder 41-95 und 165-219). Sie zu uebertragen
 *   hiesse, den Zahlungsverkehr der Kanzlei zu ermoeglichen — den fuehren wir
 *   selbst. Ausserdem sind es Bankdaten von 1.511 Personen. Und die Falle, die
 *   die Feldtafel nennt: Eine als Bankleitzahl uebergebene **`0` LOESCHT** die
 *   Bankverbindung im DATEV-Stammsatz, wenn die entsprechende Einstellung
 *   aktiv ist. Ein Erzeuger, der Felder vorsichtshalber mit `0` fuellt,
 *   richtet Schaden an. Dass hier durchgehend {@link FELD_LEER} steht und
 *   nirgends eine `'0'`, ist genau diese Zusage.
 * - **Die Mahnfristen** (250-254). Das Mahnwesen bleibt im Haus.
 * - **Die E-Mail-Adresse** (Feld 33). Siehe den Plan-Nachtrag zu Task 7:
 *   Sie ist eine personenbezogene Angabe, die fuer den Ausgleich offener
 *   Posten nicht gebraucht wird, und Feld 99 beantwortet die eine Rueckfrage,
 *   die sie ersparen sollte, bereits.
 * - **Kurzbezeichnung, Anrede, Adressart, Postfach, Kreditlimit,
 *   Zahlungsbedingung.** Nichts davon fuehrt dieses System; ein gefuelltes
 *   Feld ohne Zweck ist Zeichenrauschen, das der Steuerberater erst deuten und
 *   dann ignorieren muss (dieselbe Begruendung wie `E-1022` fuer Belegfeld 2).
 */
export function personenstammsatzFelder(satz, befunde = []) {
    pruefeKodierung(satz);
    const felder = Array.from({ length: DEBITOR_KREDITOR_FELDANZAHL }, () => FELD_LEER);
    const setze = (stelle, wert) => {
        felder[stelle - 1] = wert;
    };
    setze(DEBITOR_STELLE.konto, feldZahl(satz.konto));
    setze(DEBITOR_STELLE.adressatentyp, feldText(satz.adressatentyp));
    // Feld 2 traegt den Namen eines Unternehmens, Feld 6 den eines Partners ohne
    // Angabe. Die Felder 4 und 5 bleiben leer, solange dieses System keinen
    // getrennten Vor- und Nachnamen fuehrt (siehe ADRESSATENTYP).
    const namensstelle = satz.adressatentyp === ADRESSATENTYP.unternehmen
        ? DEBITOR_STELLE.nameUnternehmen
        : DEBITOR_STELLE.nameKeineAngabe;
    setze(namensstelle, feldText(feldGekuerzt(satz.konto, 'Name', namensstelle, satz.name, DEBITOR_MAX_ZEICHEN.nameUnternehmen, befunde)));
    if (satz.euUstId !== null) {
        setze(DEBITOR_STELLE.euLand, feldText(satz.euUstId.land));
        setze(DEBITOR_STELLE.euUstId, feldText(feldGekuerzt(satz.konto, 'EU-UStID', DEBITOR_STELLE.euUstId, satz.euUstId.nummer, DEBITOR_MAX_ZEICHEN.euUstId, befunde)));
    }
    setze(DEBITOR_STELLE.strasse, feldText(feldGekuerzt(satz.konto, 'Strasse', DEBITOR_STELLE.strasse, satz.strasse, DEBITOR_MAX_ZEICHEN.strasse, befunde)));
    setze(DEBITOR_STELLE.postleitzahl, feldText(feldGekuerzt(satz.konto, 'Postleitzahl', DEBITOR_STELLE.postleitzahl, satz.postleitzahl, DEBITOR_MAX_ZEICHEN.postleitzahl, befunde)));
    setze(DEBITOR_STELLE.ort, feldText(feldGekuerzt(satz.konto, 'Ort', DEBITOR_STELLE.ort, satz.ort, DEBITOR_MAX_ZEICHEN.ort, befunde)));
    setze(DEBITOR_STELLE.land, feldText(satz.land));
    if (satz.kundennummer !== null) {
        setze(DEBITOR_STELLE.kundennummer, feldText(feldGekuerzt(satz.konto, 'Kundennummer', DEBITOR_STELLE.kundennummer, satz.kundennummer, DEBITOR_MAX_ZEICHEN.kundennummer, befunde)));
    }
    setze(DEBITOR_STELLE.sprache, feldZahl(SPRACHE_DEUTSCH));
    setze(DEBITOR_STELLE.diverseKonto, feldZahl(DIVERSE_KONTO_NEIN));
    pruefeFeldzahl(DEBITOR_KREDITOR_FELDANZAHL, felder, 'Der Personenstammsatz');
    return felder;
}
/** Ein Stammsatz als Zeichenkette, ohne Zeilenende. */
export function baueStammsatzZeile(satz, befunde = []) {
    return csvZeile(personenstammsatzFelder(satz, befunde));
}
// ===========================================================================
// 3. SACHKONTENBESCHRIFTUNGEN — DATENKATEGORIE 20, FORMATVERSION 3
// ===========================================================================
/**
 * Die vier Felder der Datenkategorie 20, in ihrer Reihenfolge.
 *
 * **Vier und nicht zwei.** Die aeltere Fassung kannte Konto und Beschriftung;
 * die heutige nimmt zusaetzlich eine Sprach-ID und eine lange Beschriftung.
 * Diese Kategorie ist in `docs/datev/extf-felder-stammdaten.md` vollstaendig
 * beschrieben, weil sie klein ist — anders als bei den 254 Feldern der
 * Kategorie 16 gibt es hier keine benannte Luecke.
 */
export const KONTENBESCHRIFTUNG_FELDER = [
    'Konto',
    'Kontenbeschriftung',
    'Sprach-ID',
    'Kontenbeschriftung lang',
];
/** Feld 3: `de-DE`. Die zweite zugelassene Belegung waere `en-GB`. */
export const SPRACH_ID_DEUTSCH = 'de-DE';
/** Feld 2 nimmt 40 Zeichen, Feld 4 dreihundert. */
export const KONTENBESCHRIFTUNG_MAX_ZEICHEN = { kurz: 40, lang: 300 };
/**
 * Baut die vier Felder einer Kontenbeschriftung.
 *
 * Feld 2 wird auf 40 Zeichen gekuerzt, Feld 4 auf 300 — beide aus demselben
 * Namen. Dass der kurze Name eine Kuerzung des langen ist und keine eigene
 * Angabe, ist die Wahl dieses Hauses: Zwei gepflegte Namen je Konto waeren
 * zwei Wahrheiten darueber, wie das Konto heisst.
 */
export function kontenbeschriftungFelder(beschriftung, befunde = []) {
    nachZielkodierung(beschriftung.name);
    const felder = [
        feldZahl(beschriftung.konto),
        feldText(feldGekuerzt(beschriftung.konto, 'Kontenbeschriftung', 2, beschriftung.name, KONTENBESCHRIFTUNG_MAX_ZEICHEN.kurz, befunde)),
        feldText(SPRACH_ID_DEUTSCH),
        feldText(kuerzeAufZeichen(beschriftung.name, KONTENBESCHRIFTUNG_MAX_ZEICHEN.lang)),
    ];
    pruefeFeldzahl(KONTENBESCHRIFTUNG_FELDER.length, felder, 'Die Kontenbeschriftung');
    return felder;
}
/** Eine Kontenbeschriftung als Zeichenkette, ohne Zeilenende. */
export function baueKontenbeschriftungZeile(beschriftung, befunde = []) {
    return csvZeile(kontenbeschriftungFelder(beschriftung, befunde));
}
/**
 * Waehlt die Konten aus, deren Beschriftung hinausgeht.
 *
 * ===========================================================================
 * NUR INDIVIDUELL BESCHRIFTETE KONTEN — UND WARUM DAS DIE SICHERE RICHTUNG IST
 * ===========================================================================
 *
 * Die Formatbeschreibung empfiehlt ausdruecklich, **nur individuell
 * beschriftete** Konten zu liefern: Die Standardbeschriftungen verwaltet der
 * DATEV-Kontenrahmen, und sie sollen nicht ueberschrieben werden
 * (`docs/datev/extf-felder-stammdaten.md`). Ein Beschriftungsexport, der den
 * ganzen Kontenkatalog abzieht, ueberschreibt drueben jeden gepflegten Namen
 * mit unserem — und jede Abweichung, die die Kanzlei bemerkt, ist eine
 * Korrektur.
 *
 * **Zwei Signale, beide aus dem Bestand dieses Repositories gelesen und keins
 * geraten:**
 *
 * 1. **Wir haben das Konto selbst umbenannt.** Der am Periodenende gueltige
 *    Name weicht von dem ab, unter dem eine Migration das Konto angelegt hat.
 *    Der Anlagename kommt aus dem Kontenrahmen — die Seed-Migrationen sagen
 *    das von sich selbst und nennen ihre Fundstelle je Zeile.
 * 2. **Der Name war schon bei der Anlage keiner aus dem Kontenrahmen.** Das
 *    laesst sich aus dem Namensverlauf nicht ableiten und steht deshalb als
 *    eingecheckte Liste in der Naht (`apps/api/src/datev/kontenbeschriftung.ts`).
 *
 * **Was diese Regel bewusst NICHT tut:** Sie behauptet keine Abweichung, die
 * sie nicht belegen kann. Ein Konto, das wir nie umbenannt haben und das nicht
 * in der Liste steht, geht nicht hinaus — auch wenn sein Name drueben ein
 * anderer sein sollte. Das ist die konservative Richtung: Eine ueberfluessige
 * Zeile ueberschreibt eine gepflegte Bezeichnung, eine fehlende laesst sie
 * stehen.
 */
export function istIndividuellBeschriftet(konto) {
    if (konto.hausbezeichnet)
        return true;
    if (konto.standardbezeichnung === null)
        return false;
    return konto.name !== konto.standardbezeichnung;
}
/** Ein Zeitpunkt, den die Auswahlregel nicht vergleichen kann. */
export class DatevStammdatenzeitfehler extends Error {
    wert;
    constructor(wert, wo) {
        super(`${wo} ist mit ${JSON.stringify(wert)} kein Zeitpunkt der Form ` +
            `JJJJ-MM-TTTHH:MM:SS.mmmZ. Die Auswahl vergleicht Zeichenketten und nicht ` +
            `Date-Werte: Ein Date brauchte eine Zeitzone, und zwei Maschinen mit ` +
            `verschiedener Einstellung waehlten dann verschiedene Saetze aus.`);
        this.wert = wert;
        this.name = 'DatevStammdatenzeitfehler';
    }
}
const ISO_ZEITPUNKT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
function pruefeZeitpunkt(wert, wo) {
    if (!ISO_ZEITPUNKT.test(wert))
        throw new DatevStammdatenzeitfehler(wert, wo);
}
/**
 * Vollexport beim ersten Mal, danach nur die geaenderten (`D-14`, `E-1027`).
 *
 * **Warum ueberhaupt zwei Arten.** Gemessen sind **341 von 1.511**
 * Personenkonten ueberhaupt bebucht; die allermeisten Kunden haben in einer
 * gegebenen Periode keine einzige Buchung. Ein Stammdatenexport, der jedes Mal
 * alle schickt, erzeugte bei jedem Import **1.170 Aenderungsmeldungen ueber
 * nichts** — und eine Meldung, die niemand mehr liest, verdeckt die eine, die
 * zaehlt.
 *
 * **Warum die Auswahl hier steht und nicht in einer `WHERE`-Klausel.** Sie ist
 * die Zusicherung `E-1027` selbst, und sie soll an genau einer Stelle stehen,
 * die ohne Datenbank pruefbar ist. Die Naht liest alle Saetze — bei 1.511
 * Zeilen ist das nichts — und laesst diese Funktion entscheiden. Waere die
 * Regel eine `WHERE`-Zeile, muesste jeder Aufrufer sie richtig schreiben, und
 * der zweite schriebe `>=` statt `>`.
 *
 * **`>` und nicht `>=`.** Der uebergebene Zeitpunkt ist der eines
 * ABGESCHLOSSENEN Laufs; ein Satz, der in derselben Millisekunde geaendert
 * wurde, war in ihm enthalten. `>=` schickte ihn ein zweites Mal — genau die
 * Meldung ueber nichts, die die Trennung vermeiden soll.
 *
 * **Ein neu angelegter Kunde braucht keinen eigenen Zweig:** Sein
 * Aenderungszeitpunkt ist nie aelter als sein Anlagezeitpunkt (Migration
 * `0016` setzt `updated_at` bei jedem Schreibvorgang), also faellt er schon
 * ueber diesen Vergleich in den Lauf.
 */
export function waehleStammsaetze(alle, letzterLaufAm) {
    for (const satz of alle)
        pruefeZeitpunkt(satz.geaendertAm, 'Der Aenderungszeitpunkt');
    if (letzterLaufAm === null) {
        return { art: 'voll', seit: null, saetze: [...alle], uebergangen: 0 };
    }
    pruefeZeitpunkt(letzterLaufAm, 'Der Zeitpunkt des letzten Laufs');
    const saetze = alle.filter((satz) => satz.geaendertAm > letzterLaufAm);
    return {
        art: 'aenderung',
        seit: letzterLaufAm,
        saetze,
        uebergangen: alle.length - saetze.length,
    };
}
//# sourceMappingURL=stammdaten.js.map